// The sections of the "Withdrawal of Intimation / Resignation" form that show
// the ORIGINAL resignation (Employee Details + Original Resignation Info, with
// a "View Complete Details" link to the full resignation form), plus a
// read-only rendering of the withdrawal request itself. Shared so the
// employee's own withdrawal form (WithdrawalResignation.tsx) and every
// approval / review screen (ApprovalDetail.tsx) show exactly the same thing.
import React from "react";
import { SectionCard, FieldLabel, ReadOnlyField, WarningBanner } from "./components";
import { EmployeeDetails, formatDate } from "../services/eSeparation.service";

/** Dates coming from find-one are already formatted (dd-mm-yyyy) or "—". */
const showDate = (v?: string | null): string => (v && v !== "—" ? v : "—");

/** Raw ISO dates (e.g. withdrawal_date) get formatted; pre-formatted ones pass through. */
const fmtAnyDate = (v?: string | null): string => {
  if (!v || v === "—") return "—";
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? formatDate(v.slice(0, 10)) : v;
};

/**
 * Name + Emp. No. of the Controlling Officer (Dy. HOD). find-one sometimes has
 * a null `co_name`, so fall back to the first approver in the history ledger
 * (level-1 approval) and finally to the Dy. HOD.
 */
const getControllingOfficer = (r: any): string => {
  if (!r) return "";
  const firstApprover = (r.history || []).find(
    (h: any) => String(h.action).toUpperCase() === "APPROVE"
  );
  const name = r.co_name || firstApprover?.approver_name || r.dy_hod_name || "";
  const empNo = r.co_emp_no || firstApprover?.approver_emp_id || r.dy_hod_emp_no || "";
  if (!name) return "";
  return empNo ? `${name} (${empNo})` : String(name);
};

/**
 * "Last / most senior approving authority" of the resignation. Prefer the CA
 * step from find-one; otherwise walk the hierarchy (Director → HOD → Dy. HOD →
 * Controlling Officer) picking the first person actually present.
 */
const getApprovingAuthority = (r: any): string => {
  if (!r) return "";
  if (r.approving_authority) return r.approving_authority;
  const pairs: [string | null, string | null][] = [
    [r.director_name, r.director_designation],
    [r.hod_name, r.hod_designation],
    [r.dy_hod_name, r.dy_hod_designation],
    [r.co_name, r.co_designation],
  ];
  const found = pairs.find(([name]) => name);
  if (!found) return "";
  const [name, desig] = found;
  return desig ? `${name} (${desig})` : String(name);
};

export const WithdrawalOriginalSections: React.FC<{
  /** The ORIGINAL resignation record (same flattened shape getResignationDetails returns). */
  rec: any;
  /** SAP master data of the employee. */
  emp: EmployeeDetails | null;
  /** Opens the original resignation's full form; the button is hidden when omitted. */
  onViewComplete?: () => void;
  bannerMessage?: string;
}> = ({
  rec,
  emp,
  onViewComplete,
  bannerMessage = "These details are auto-fetched from your original resignation request.",
}) => {
  // find-one gives a resolved `designation_text`; fall back to master data.
  const designation = rec?.designation_text || emp?.designation || rec?.designation || "";
  const department = emp?.posting || rec?.posting || "";
  const placeOfPosting = emp?.place_of_posting || "";
  const deptPlace = [department, placeOfPosting].filter(Boolean).join(" / ");

  const empName = rec?.emp_name || emp?.emp_name || "";
  const empNo = rec?.emp_no || emp?.emp_no || "";
  const empType = rec?.emp_type || emp?.emp_type || "";
  const payScale = rec?.pay_scale || emp?.pay_scale || "";
  // find-one dates are pre-formatted; master-data doj is raw and needs formatting.
  const doj = rec?.doj || (emp?.doj ? formatDate(emp.doj) : "");
  const mobile = rec?.mobile || emp?.mobile || "";
  const email = rec?.email || emp?.email || "";

  return (
    <>
      <SectionCard index={0} title="Employee Details">
        <WarningBanner type="info" message={bannerMessage} />
        <div className="row g-3">
          <div className="col-md-6">
            <FieldLabel label="Name of the Employee" />
            <ReadOnlyField value={empName} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Emp. No." />
            <ReadOnlyField value={empNo} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Designation" />
            <ReadOnlyField value={designation} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Regular / Contract" />
            <ReadOnlyField value={empType} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Deptt. / Place of Posting" />
            <ReadOnlyField value={deptPlace} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Pay Scale" />
            <ReadOnlyField value={payScale} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Date of Joining DMRC" />
            <ReadOnlyField value={showDate(doj)} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Mobile No. of Employee" />
            <ReadOnlyField value={mobile} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Email address of Employee" />
            <ReadOnlyField value={email} />
          </div>
        </div>
      </SectionCard>

      <SectionCard index={1} title="Original Resignation Info">
        <div className="row g-3">
          <div className="col-md-6">
            <FieldLabel label="Name of Controlling Officer and Emp. No. (Dy. HOD)" />
            <ReadOnlyField value={getControllingOfficer(rec)} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Date of Intimation of Resignation" />
            <ReadOnlyField value={showDate(rec?.intimation_date)} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Date of Completion of Notice Period (90 days)" />
            <ReadOnlyField value={showDate(rec?.notice_end_date)} />
          </div>
          <div className="col-md-6">
            <FieldLabel label="Name and Designation of Approving Authority for Resignation" />
            <ReadOnlyField value={getApprovingAuthority(rec)} />
          </div>
        </div>
        {rec?.request_id && onViewComplete && (
          <div className="mt-3">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={onViewComplete}
            >
              <i className="ti ti-file-text me-1" />
              View Complete Details
            </button>
          </div>
        )}
      </SectionCard>
    </>
  );
};

/** Read-only "Withdrawal Request" section, as filed by the employee. */
export const WithdrawalRequestViewSection: React.FC<{
  index?: number;
  withdrawalDate?: string | null;
  reason?: string | null;
}> = ({ index = 2, withdrawalDate, reason }) => (
  <SectionCard index={index} title="Withdrawal Request">
    <div className="row g-3">
      <div className="col-12">
        <FieldLabel label="Submission of Application for Withdrawal of Intimation of Resignation" />
        <ReadOnlyField value={fmtAnyDate(withdrawalDate)} />
      </div>
      <div className="col-12">
        <FieldLabel label="Reason for Withdrawal" />
        <div className="p-3 rounded-3 bg-light border" style={{ whiteSpace: "pre-wrap" }}>
          {reason || "—"}
        </div>
      </div>
    </div>
  </SectionCard>
);
